import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL, TEXTURE_IMG } from "@/lib/constants";
import { toast } from "sonner";
import { ArrowRight, Loader2, Eye, EyeOff } from "lucide-react";

export default function DealerLogin() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [u, setU] = useState("");
  const [p, setP] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);

  useEffect(() => { document.title = "Bayi Girişi – Evser Halı"; }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!u || !p) {
      toast.error("Kullanıcı adı ve şifre gerekli");
      return;
    }
    setLoading(true);
    try {
      const res = await login(u.trim(), p);
      toast.success("Giriş başarılı");
      nav(res.role === "admin" ? "/admin" : "/bayi", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Giriş başarısız");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
      {/* left visual */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 bg-[#1a1a1a] text-[#f7f5f0] overflow-hidden">
        <img src={TEXTURE_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#1a1a1a]/40 to-[#1a1a1a]" />
        <div className="relative">
          <Link to="/" className="inline-flex items-center gap-3" data-testid="login-logo-link">
            <img src={LOGO_URL} alt="" className="h-14 w-auto bg-white p-1.5" />
            <div>
              <div className="font-serif text-2xl">Evser Halı</div>
              <div className="text-[0.65rem] uppercase tracking-[0.25em] text-[#c8a97e]">B2B Portal</div>
            </div>
          </Link>
        </div>
        <div className="relative">
          <span className="label-tag" style={{ color: "#c8a97e" }}>Bayi Portalı</span>
          <h2 className="font-serif text-5xl md:text-6xl leading-none mt-4">
            Geleneksel<br /><em className="text-[#c8a97e] not-italic">dokuma</em>,<br />modern sipariş.
          </h2>
          <p className="mt-8 max-w-md text-[#d4cfc4] leading-relaxed">
            Bayilerimize özel sipariş ve stok yönetim sistemi. Mağazalarınız için
            tüm Evser Halı koleksiyonuna anında erişim.
          </p>
        </div>
        <div className="relative text-xs uppercase tracking-[0.3em] text-[#a09a8e]">
          © {new Date().getFullYear()} – Evser Halı
        </div>
      </div>

      {/* right form */}
      <div className="flex flex-col p-8 md:p-16 bg-[#f7f5f0]">
        <div className="lg:hidden mb-12">
          <Link to="/" className="inline-flex items-center gap-3">
            <img src={LOGO_URL} alt="" className="h-12 w-auto" />
            <span className="font-serif text-xl">Evser Halı</span>
          </Link>
        </div>
        <div className="m-auto w-full max-w-md">
          <span className="label-tag">Giriş</span>
          <h1 className="font-serif text-4xl md:text-5xl mt-4">Bayi Girişi</h1>
          <p className="mt-3 text-[#6b655c]">
            Hesabınıza giriş yapın. Yönetici ve bayi girişleri buradan yapılır.
          </p>
          <form onSubmit={onSubmit} className="mt-12 space-y-8" data-testid="login-form">
            <div>
              <label className="text-xs uppercase tracking-[0.25em] text-[#6b655c]">Kullanıcı Adı</label>
              <input
                type="text"
                value={u}
                onChange={(e) => setU(e.target.value)}
                className="mt-3 w-full bg-transparent border-b border-[#1a1a1a]/40 focus:border-[#c8a97e] outline-none py-3 text-lg"
                autoComplete="username"
                data-testid="login-username-input"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.25em] text-[#6b655c]">Şifre</label>
              <div className="relative">
                <input
                  type={showPw ? "text" : "password"}
                  value={p}
                  onChange={(e) => setP(e.target.value)}
                  className="mt-3 w-full bg-transparent border-b border-[#1a1a1a]/40 focus:border-[#c8a97e] outline-none py-3 pr-10 text-lg"
                  autoComplete="current-password"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={() => setShowPw((v) => !v)}
                  aria-label={showPw ? "Şifreyi gizle" : "Şifreyi göster"}
                  className="absolute right-0 bottom-3 text-[#6b655c] hover:text-[#1a1a1a] transition-colors p-1"
                  data-testid="login-password-toggle"
                >
                  {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="btn-luxe w-full justify-center disabled:opacity-60"
              data-testid="login-submit-btn"
            >
              {loading ? <Loader2 className="animate-spin" size={16} /> : <>Giriş Yap <ArrowRight size={16} /></>}
            </button>
          </form>
          <div className="mt-8">
            <Link to="/" className="text-sm text-[#6b655c] hover:text-[#c8a97e]" data-testid="login-back-home-link">
              ← Anasayfaya dön
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
